// panelDragGeometry — pointer drag による「パネル並べ替え / ドック間移動」の純ロジック
// (docs/_wip/floating-panels-2026-08-01.md §3 決定2「pointer drag へ全面移行」、S1)。
//
// 背景: `ModernPanelDragShell` は元々ネイティブ HTML5 DnD (`draggable` /
// `dragover` / `drop`) で、各パネルシェル・region コンテナが自分の `onDragOver` で
// 「ポインタが自分の上にあるか」を受け取っていた (= ブラウザがヒットテストして
// くれる)。pointer events + `setPointerCapture` (S1 決定2) では、掴んだ grip 要素に
// 以後の pointermove が **集約**される (= 画面外・他要素の上でも grip 自身が受け取る)
// ため、「今どのパネル/region の上にいるか」はブラウザに聞けず自前でヒットテストする
// 必要がある。
//
// この純ロジックは「矩形の集合 + ポインタ座標 → drop 候補」の判定 (= 旧
// `ModernPanelDragShell.onDragOver` インラインの上半分/下半分判定 + region 末尾
// フォールバックを 1 箇所に集約したもの) と、「pointerdown からの移動量 → ドラッグ
// 開始判定」を担う。**DOM 非依存** (矩形は呼び出し側が `getBoundingClientRect()` 等で
// 計測して渡す) なので node テストで固定できる (`oekaki/CLAUDE.md` jsdom race 回避の
// 定石と同じ流儀、`floatingSlot.ts` 参照)。
//
// **S2/S3 拡張点**: region/panel の id 型はジェネリクスにしてあり、Modern の
// `WorkspaceRegion`/`PanelId` に依存しない (= Classic の自由配置 (S3) や、フロート
// 判定 (S2、「ドック矩形 + 吸着マージンの内/外」) が同じ型でここに相乗りできる)。
//
// S2 (floating-panels-2026-08-01.md §2 決定1「ドック優先の吸着」、2026-08-01) で
// `resolveDockSnapTarget` を追加した。`resolvePanelDropTarget` が null (= どのパネル/region
// 矩形にもポインタが直接乗っていない) を返したときの救済判定: region 矩形から一定距離
// (既定 `DOCK_SNAP_MARGIN_PX`) 以内ならドック側が勝つ (= 「端に置きたかったのに浮いた」誤爆
// を防ぐ)。これでも見つからなければ真のフロート候補 (呼び出し側が float プレビューへ倒す)。

/** ポインタ座標 (client 系、`PointerEvent.clientX/Y` 互換)。 */
export interface DragPoint {
  readonly x: number;
  readonly y: number;
}

/** DOM 計測済みの矩形 (`getBoundingClientRect` 互換の left/top/width/height)。 */
export interface DragRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * ドラッグ開始判定の既定閾値 (px)。この値未満の移動は「クリック」扱いにする
 * (= グリップ帯上での誤ドラッグ防止。数 px 未満のブレは pointerdown→pointerup の
 * 通常クリックでも発生するため)。
 */
export const DRAG_START_THRESHOLD_PX = 6;

/**
 * pointerdown 開始位置からの移動量が閾値を超えたか (= ドラッグ開始判定の純関数)。
 * 平方距離で比較する (= sqrt を避ける、閾値越え判定に精度は不要)。
 */
export const exceedsDragThreshold = (
  start: DragPoint,
  current: DragPoint,
  thresholdPx: number = DRAG_START_THRESHOLD_PX,
): boolean => {
  const dx = current.x - start.x;
  const dy = current.y - start.y;
  return dx * dx + dy * dy >= thresholdPx * thresholdPx;
};

/** 矩形にポインタが含まれるか (= 境界を含む、`DOMRect` の座標系に準拠)。 */
const containsPoint = (rect: DragRect, pointer: DragPoint): boolean =>
  pointer.x >= rect.left &&
  pointer.x <= rect.left + rect.width &&
  pointer.y >= rect.top &&
  pointer.y <= rect.top + rect.height;

/**
 * 「パネル単位の before/after 判定」候補 1 件 (= 1 パネルシェルの矩形 + 所属 region/index)。
 * 呼び出し側が現在 DOM 上にあるパネルシェル全部を集めて渡す (= region をまたいだ集合)。
 */
export interface PanelDropCandidate<TRegion extends string, TPanel extends string> {
  readonly panelId: TPanel;
  readonly region: TRegion;
  /** この region 内でのパネルの現在 index (= movePanel の toIndex 基準)。 */
  readonly index: number;
  readonly rect: DragRect;
}

/**
 * 「region 末尾余白」候補 1 件 (= 列末尾 / 空 region へのドロップ、旧
 * `handleRegionDragOverEnd` フォールバックに対応)。
 */
export interface RegionEndCandidate<TRegion extends string> {
  readonly region: TRegion;
  /** 末尾挿入位置 (= その region の現在の可視パネル数)。 */
  readonly endIndex: number;
  readonly rect: DragRect;
}

/**
 * `resolvePanelDropTarget` の判定結果。呼び出し側は `kind` で分岐して自分の
 * 意思決定関数 (before/after 用 / 末尾用) へ振り分ける。
 *
 * `kind: 'panel'` の `index` はヒットした **パネル自身の生 index** (= `insertAfter` に
 * よる +1 調整はしない)。旧 `handlePanelDragOver(region, index, insertAfter)` シグネチャ
 * (呼び出し側で `insertAfter ? index + 1 : index` を計算する) にそのまま渡せるようにする
 * ためで、最終的な挿入位置の確定は呼び出し側の責務のまま変えない。
 */
export type PanelDropResolution<TRegion extends string> =
  | {
      readonly kind: 'panel';
      readonly region: TRegion;
      readonly index: number;
      readonly insertAfter: boolean;
    }
  | { readonly kind: 'region-end'; readonly region: TRegion; readonly endIndex: number }
  | null;

/**
 * ポインタ座標から drop 候補を判定する純関数 (= DOM 非依存、旧ネイティブ dragover の
 * ヒットテストを手動で再現する)。
 *
 * 判定順序 (旧 `ModernPanelDragShell.onDragOver` + `renderRegion` 列末尾フォールバック
 * と同じ優先度):
 *   1. いずれかのパネル矩形にポインタが入っていれば、そのパネルの上半分/下半分で
 *      before(index) / after(index+1) を返す。`excludePanelId` (= 今掴んでいるパネル
 *      自身) は候補から除く (= 自分の上へのドロップは無意味)。
 *   2. どのパネル矩形にも入っていなければ、region 矩形 (= 列コンテナの余白を含む全体)
 *      にポインタが入っている region を「末尾挿入」候補として返す。
 *   3. どちらにも入っていなければ `null` (= 直前の drop 候補を変えない。呼び出し側の
 *      state はそのまま = 旧「dragover が来ない間は dropTarget が変化しない」と同じ)。
 *
 * `panels` を先に総当たりする (= region より優先) ので、region 矩形がパネル矩形を
 * 内包していても常にパネル判定が勝つ (通常配置は必ずそう)。
 */
export const resolvePanelDropTarget = <TRegion extends string, TPanel extends string>(
  pointer: DragPoint,
  panels: ReadonlyArray<PanelDropCandidate<TRegion, TPanel>>,
  regionEnds: ReadonlyArray<RegionEndCandidate<TRegion>>,
  excludePanelId: TPanel | null,
): PanelDropResolution<TRegion> => {
  for (const p of panels) {
    if (p.panelId === excludePanelId) continue;
    if (!containsPoint(p.rect, pointer)) continue;
    const insertAfter = pointer.y > p.rect.top + p.rect.height / 2;
    return { kind: 'panel', region: p.region, index: p.index, insertAfter };
  }
  for (const r of regionEnds) {
    if (containsPoint(r.rect, pointer)) {
      return { kind: 'region-end', region: r.region, endIndex: r.endIndex };
    }
  }
  return null;
};

// ── S2 (floating-panels-2026-08-01.md §2 決定1「ドック優先の吸着」) ─────────────────

/**
 * ドック吸着マージンの既定値 (px、§2「マージン ~40px、実装時に実機調整」)。region 矩形の外
 * でもこの距離以内ならドック側が勝つ (= 端へ置きたいのに浮いてしまう誤爆を防ぐ)。
 */
export const DOCK_SNAP_MARGIN_PX = 40;

/** 矩形の外側にある点までの最短距離 (px)。点が矩形内なら 0。 */
const distanceToRect = (rect: DragRect, pointer: DragPoint): number => {
  const dx = Math.max(rect.left - pointer.x, 0, pointer.x - (rect.left + rect.width));
  const dy = Math.max(rect.top - pointer.y, 0, pointer.y - (rect.top + rect.height));
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * `resolvePanelDropTarget` が null (= どの矩形にも直接ヒットしない) だったときの吸着救済判定。
 * region 矩形からの最短距離が `marginPx` 以内の region のうち、**最も近いもの** を「末尾へ
 * 挿入」候補として返す (= 複数の region がマージン内にある場合の tie-break)。1 つも無ければ
 * `null` (= 真のフロート候補、呼び出し側が float プレビューへ倒す)。
 *
 * `resolvePanelDropTarget` と同じ矩形集合 (regionEnds) を再利用する設計 (= 二重計測しない)。
 * 直接ヒット判定 (距離 0) は `resolvePanelDropTarget` 側が先に拾う前提なので、本関数は
 * 「呼び出し側で resolvePanelDropTarget が null を返した後」にだけ呼ぶ想定 (= 呼び出し順の
 * 責務は呼び出し側)。
 */
export const resolveDockSnapTarget = <TRegion extends string>(
  pointer: DragPoint,
  regionEnds: ReadonlyArray<RegionEndCandidate<TRegion>>,
  marginPx: number = DOCK_SNAP_MARGIN_PX,
): RegionEndCandidate<TRegion> | null => {
  let best: RegionEndCandidate<TRegion> | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const r of regionEnds) {
    const dist = distanceToRect(r.rect, pointer);
    if (dist <= marginPx && dist < bestDist) {
      bestDist = dist;
      best = r;
    }
  }
  return best;
};

// ── S2 §11 (floating-panels-2026-08-01.md、consult 2026-08-01 追加確定): エッジスナップ ──
//
// 動機: collapse した (= パネルが 1 枚も無い) region は `regionEnds`/`panels` 候補として
// 存在しないため、`resolvePanelDropTarget` も `resolveDockSnapTarget` も判定材料を持たず、
// 「ドラッグで出したフロートを画面端へ運んでもドックできない」行き止まりが生じる
// (§11 動機1)。エッジスナップは **layout row の矩形 (= 常に存在する) だけを基準**にした
// 画面端判定なので、列の中身に依存せず常に成立する。

/** エッジスナップの既定しきい値 (px、§11「初期値 64px、定数化して実機調整」)。 */
export const EDGE_SNAP_THRESHOLD_PX = 64;

/**
 * pointer が layout row の左右端から `thresholdPx` 以内にあるかを判定する (= 画面端基準、
 * 列の矩形に一切依存しない)。row の縦範囲外 (= ヘッダ等へはみ出た pointer) では不成立
 * (`railSlotFromPointer` 呼び出し元の `updateRailDropSlot` と同型の「row の外なら無効」
 * ガード)。左右どちらの帯にも入っていなければ `null`。
 *
 * `resolvePanelDropTarget` / `resolveDockSnapTarget` の**両方が null を返した後**にのみ
 * 呼ぶ想定 (= 優先順位 ①パネル直接ヒット → ②40px 吸着 → ③エッジスナップ → ④フロート、
 * 呼び出し順の責務は呼び出し側)。
 */
export const resolveEdgeSnapSide = (
  pointer: DragPoint,
  rowRect: DragRect,
  thresholdPx: number = EDGE_SNAP_THRESHOLD_PX,
): 'left' | 'right' | null => {
  if (pointer.y < rowRect.top || pointer.y > rowRect.top + rowRect.height) return null;
  if (pointer.x <= rowRect.left + thresholdPx) return 'left';
  if (pointer.x >= rowRect.left + rowRect.width - thresholdPx) return 'right';
  return null;
};
